import { useContext, useRef } from "react";
import { blueConfig } from "../config";
import { ctx } from "../ctx";
const { serviceUUID, characteristicUUID } = blueConfig;

export function useBlue() {
  const bluetoothDevice = useRef<BluetoothDevice | null>(null);
  const characteristic = useRef<BluetoothRemoteGATTCharacteristic | null>(null);

  const { setData } = useContext(ctx);

  async function connect(onDisconnect: () => void) {
    if ("bluetooth" in navigator === false) {
      throw new Error("Bluetooth not supported in this browser.");
    }

    try {
      setData({ loading: true });
      bluetoothDevice.current = await navigator.bluetooth.requestDevice({
        optionalServices: [serviceUUID],
        acceptAllDevices: true,
      });

      if (!bluetoothDevice.current.gatt) {
        throw new Error("Bluetooth device does not support GATT.");
      }

      const server = await bluetoothDevice.current.gatt!.connect();
      if (!server) {
        throw new Error("Failed to connect to GATT server.");
      }

      const service = await server.getPrimaryService(serviceUUID);
      characteristic.current = await service.getCharacteristic(
        characteristicUUID
      );
      await characteristic.current!.startNotifications();

      bluetoothDevice.current.addEventListener("gattserverdisconnected", () => {
        onDisconnect();
      });
    } catch (error) {
      console.error("Error connecting to Bluetooth device:", error);
      throw error;
    } finally {
      setData({ loading: false });
    }
  }

  async function sendMessage(message: string) {
    if (
      !bluetoothDevice.current ||
      !bluetoothDevice.current.gatt ||
      !characteristic.current
    ) {
      throw new Error(
        "Bluetooth device not connected or characteristic not available."
      );
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    await characteristic.current.writeValue(data as any);
  }

  return {
    connect,
    initialized: bluetoothDevice.current !== null,
    sendMessage,
  };
}
